import { useState } from "react";
import { Alert, Badge, Button, Input, Modal, Table, type TableColumn } from "../../ds";
import {
  ApiError,
  api,
  errorText,
  type DeviceDetail,
  type LanClient,
  type PingResult,
} from "../../lib/api";
import { fmtDateTime } from "../../lib/format";
import { SectionCard } from "../SectionCard";
import { useDeviceAction } from "../../lib/hooks/useDeviceAction";
import { useSection } from "../../lib/hooks/useSection";

// Sektion „LAN-Clients“ → action `lan_clients`, plus `ping` und
// `static_lease_set` je Client.

interface PingState {
  loading: boolean;
  result?: PingResult;
  error?: string;
}

function PingCell({ state }: { state: PingState | undefined }) {
  if (!state || state.loading) return null;
  if (state.error) return <span className="text-xs text-danger-fg">{state.error}</span>;
  const r = state.result;
  if (!r) return null;
  const lost = r.lossPct >= 100;
  return (
    <Badge variant={lost ? "danger" : r.lossPct > 0 ? "warn" : "success"}>
      {r.lossPct.toLocaleString("de-DE")} % Verlust
      {r.rttAvgMs !== null && !lost ? ` · ⌀ ${r.rttAvgMs.toLocaleString("de-DE")} ms` : ""}
    </Badge>
  );
}

/** Die Tabelle als eigene Einheit — die Komponente daneben hält nur noch die
 *  beiden Aktionen und ihren Zustand. */
function clientColumns(
  device: DeviceDetail,
  pings: Record<string, PingState>,
  busyMac: string | null,
  onPing: (c: LanClient) => void,
  onToggleStatic: (c: LanClient) => void,
): TableColumn<LanClient>[] {
  return [
    {
      key: "online",
      header: "Status",
      cell: (c) => (
        <Badge variant={c.online ? "success" : "neutral"} dot>
          {c.online ? "online" : "offline"}
        </Badge>
      ),
    },
    { key: "ip", header: "IP", mono: true, cell: (c) => c.ip },
    { key: "mac", header: "MAC", mono: true, hideOnMobile: true, cell: (c) => c.mac },
    { key: "hostname", header: "Hostname", cell: (c) => c.hostname || "—" },
    { key: "iface", header: "Interface", hideOnMobile: true, cell: (c) => c.iface ?? "—" },
    {
      key: "lease",
      header: "Lease bis",
      hideOnMobile: true,
      cell: (c) => (
        <span className="flex flex-wrap items-center gap-2">
          {fmtDateTime(c.leaseExpires)}
          {c.static && <Badge variant="info">statisch</Badge>}
        </span>
      ),
    },
    {
      key: "ping",
      header: "Erreichbarkeit",
      align: "right",
      className: "actions",
      cell: (c) => (
        <div className="flex items-center justify-end gap-2">
          <PingCell state={pings[c.ip]} />
          <Button
            size="sm"
            variant="ghost"
            iconName={c.static ? "unlock" : "lock"}
            disabled={!device.online || !c.ip || busyMac !== null}
            loading={busyMac === c.mac}
            title={
              c.static
                ? "Feste Zuordnung aufheben — die Adresse kommt zurück in den Pool"
                : "Diese Adresse fest an die MAC binden (config host)"
            }
            onClick={() => onToggleStatic(c)}
          >
            {c.static ? "Statisch aufheben" : "Statisch setzen"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            iconName="activity"
            disabled={!device.online}
            loading={pings[c.ip]?.loading}
            onClick={() => onPing(c)}
          >
            Ping
          </Button>
        </div>
      ),
    },
  ];
}

/** Beim Festnageln darf der Name mit: dnsmasq trägt ihn als DNS-Namen ein, und
 *  oft ist das die einzige Gelegenheit, einem Gerät ohne eigenen Hostnamen
 *  (Wallbox, Shelly) überhaupt einen zu geben. */
function StaticLeaseModal({
  client,
  hostname,
  busy,
  onChange,
  onClose,
  onSubmit,
}: {
  client: LanClient | null;
  hostname: string;
  busy: boolean;
  onChange: (v: string) => void;
  onClose: () => void;
  onSubmit: () => void;
}) {
  return (
    <Modal
      open={client !== null}
      onClose={onClose}
      title="Adresse fest vergeben"
      subtitle={
        client
          ? `${client.ip} bleibt danach fest bei ${client.mac} — auch nach Lease-Ablauf und Neustart.`
          : undefined
      }
      size="sm"
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" iconName="check" loading={busy} onClick={onSubmit}>
            Fest vergeben
          </Button>
        </>
      }
    >
      <Input
        label="Hostname (optional)"
        placeholder="wallbox-hof"
        helperText="Löst danach im LAN auf. Ohne Punkt — die Domain hängt dnsmasq selbst an. Leer heißt: nur die Adresse, kein Name."
        value={hostname}
        autoFocus
        onChange={(e) => onChange(e.target.value)}
      />
    </Modal>
  );
}

export function LanSection({ device }: { device: DeviceDetail }) {
  const section = useSection<{ clients: LanClient[] }>(device, "lan");
  const { note, setNote, run } = useDeviceAction(device.id);
  const [pings, setPings] = useState<Record<string, PingState>>({});
  // Der Busy-Zustand aus `useDeviceAction` nennt die Aktion, hier ist aber die
  // ZEILE gemeint — sonst drehte sich jeder Statisch-Button gleichzeitig.
  const [busyMac, setBusyMac] = useState<string | null>(null);
  // Der Client, für den der Dialog offen ist — und der Name, der mitgehen soll.
  const [pending, setPending] = useState<LanClient | null>(null);
  const [hostname, setHostname] = useState("");

  async function ping(client: LanClient) {
    setPings((p) => ({ ...p, [client.ip]: { loading: true } }));
    try {
      const r = await api.runAction<PingResult>(device.id, "ping", { target: client.ip, count: 4 });
      setPings((p) => ({ ...p, [client.ip]: { loading: false, result: r.data } }));
    } catch (e) {
      const msg = e instanceof ApiError && e.isOffline ? "Router nicht erreichbar" : errorText(e);
      setPings((p) => ({ ...p, [client.ip]: { loading: false, error: msg } }));
    }
  }

  /** Festnageln fragt erst nach dem Namen, Freigeben nicht — da gibt es nichts
   *  zu entscheiden. Der Hint der Box bringt die Domain mit („OCPP.lan“), in
   *  den Eintrag gehört nur die nackte Marke. */
  function askStatic(client: LanClient) {
    if (client.static) {
      void setStatic(client, false);
      return;
    }
    setHostname((client.hostname ?? "").split(".")[0]);
    setPending(client);
  }

  async function setStatic(client: LanClient, target: boolean, name?: string) {
    setBusyMac(client.mac);
    const data = await run<{ static: boolean }>(
      "static_lease_set",
      { mac: client.mac, ip: client.ip, hostname: name || undefined, static: target },
      target
        ? `${client.ip} bleibt jetzt fest bei ${client.mac}.`
        : `${client.ip} wird wieder dynamisch vergeben.`,
    );
    setBusyMac(null);
    setPending(null);
    if (!data) return;
    // Was geschrieben wurde, ist bekannt — die Zeile lokal nachziehen spart den
    // Frame, den ein „Vom Router laden“ über LTE kosten würde. Der Zeitstempel
    // bleibt der der letzten Abfrage: neu ist die eine Zeile, nicht der Stand.
    const clients = section.data?.clients ?? [];
    section.patch(
      {
        clients: clients.map((c) =>
          c.mac === client.mac ? { ...c, static: target, hostname: name || c.hostname } : c,
        ),
      },
      section.fetchedAt ?? undefined,
    );
  }

  return (
    <>
      <SectionCard
        title="LAN-Clients"
        description="DHCP-Leases + ARP-Nachbarn"
        section={section}
        deviceOnline={device.online}
        bodyPadding={false}
        above={
          note && (
            <Alert variant={note.variant} dismissible onDismiss={() => setNote(null)}>
              {note.text}
            </Alert>
          )
        }
      >
        {(data) => (
          <Table
            columns={clientColumns(
              device,
              pings,
              busyMac,
              (c) => void ping(c),
              (c) => askStatic(c),
            )}
            rows={data.clients ?? []}
            rowKey={(c) => c.mac || c.ip}
            emptyMessage="Keine Clients im LAN gefunden."
          />
        )}
      </SectionCard>

      <StaticLeaseModal
        client={pending}
        hostname={hostname}
        busy={busyMac !== null}
        onChange={setHostname}
        onClose={() => setPending(null)}
        onSubmit={() => pending && void setStatic(pending, true, hostname.trim())}
      />
    </>
  );
}
